import { DirectUpload } from "@rails/activestorage";
import { readAndCompressImage } from 'browser-image-resizer';
import * as helpers from "@/utils/helpers";
import api from "@/utils/api";

/**
 * Stimulus 컨트롤러 인스턴스를 받아 Editor.js 이미지 도구 설정을 생성합니다.
 * @param {import("@hotwired/stimulus").Controller} controller - Stimulus 컨트롤러 인스턴스 (`this`)
 * @returns {object} Editor.js 이미지 도구를 위한 설정 객체
 */
export function createImageUploaderConfig(controller) {

    /**
     * 파일을 직접 Active Storage로 업로드하고, 리사이징을 처리합니다.
     * @param {File} file - 업로드할 파일 객체
     * @returns {Promise<object>} Editor.js Uploader API 형식에 맞는 응답 객체
     */
    const directUploader = async (file) => {
        let resizedFile = file;

        // 원본이 이미 빈 파일이면 업로드하지 않는다.
        if (!file.size) {
            alert("이미지 파일이 비어 있어요. 다른 파일로 다시 시도해주세요.");
            return Promise.reject("Empty source file.");
        }

        // 1MB 초과 이미지 리사이징
        if (file.size > 1000000) {
            // GIF 파일의 경우 애니메이션 보존을 위해 리사이징 건너뛰기
            if (file.type === "image/gif") {
                // GIF가 3MB를 초과하는 경우 경고
                if (file.size > 3000000) {
                    alert("GIF 파일의 크기가 너무 커요. 최대 3MB 이하로 올려주세요.");
                    return Promise.reject("GIF file size exceeds 3MB.");
                }
                // GIF는 원본 그대로 업로드
                resizedFile = file;
            } else {
                // 일반 이미지 파일의 경우 기존 리사이징 로직 적용
                const fileUrl = URL.createObjectURL(file);
                const image = await helpers.readImage(fileUrl);
                const resizedBlob = await readAndCompressImage(file, {
                    maxHeight: (file.type === "image/png" ? Math.max((image.height * 0.7), 1600) : 1600),
                    maxWidth: (file.type === "image/png" ? Math.max((image.width * 0.7), 1600) : 1600),
                    quality: 0.7,
                    mimeType: file.type
                });

                resizedFile = new File([resizedBlob], file.name, { type: file.type });

                // canvas.toBlob 이 지원하지 않는 포맷(HEIC/HEIF 등)에서는 빈 Blob 이 반환될 수 있다.
                // 이 경우 0바이트 파일이 그대로 업로드되어 깨진 이미지가 저장되므로 여기서 중단한다.
                if (!resizedFile.size) {
                    alert("이 이미지 형식(예: HEIC)은 브라우저에서 변환할 수 없어요. JPG 또는 PNG 로 저장한 뒤 다시 올려주세요.");
                    return Promise.reject("Resized file is empty (unsupported format).");
                }

                if (resizedFile.size > 3000000) {
                    alert("이미지 파일의 크기가 너무 커요. 최대 3MB 이하로 올려주세요.");
                    return Promise.reject("File size exceeds 3MB after resizing.");
                }
            }
        }

        const upload = new DirectUpload(resizedFile, `/rails/active_storage/direct_uploads?space_url=${controller.data.get("space-url")}`, {
            directUploadWillStoreFileWithXHR: (xhr) => {
                xhr.setRequestHeader('x-amz-acl', 'public-read');
            }
        });

        const uploadPromise = new Promise((resolve, reject) => {
            upload.create((error, blob) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(blob);
                }
            });
        });

        return uploadPromise.then((blob) => {
            return {
                success: 1,
                file: {
                    url: `${controller.data.get("cdn-host")}/${blob.key}`,
                }
            };
        });
    };

    /**
     * Editor.js 이미지 도구 (GalleryTool, SimpleImageTool)를 위한 설정 객체
     */
    const imageToolConfig = {
        captionPlaceholder: "\t",
        endpoints: {
            prefetch: controller.data.get("space-url") === "demo" ?
                "demo" : `/api/${controller.data.get("space-url")}/notes/image_blocks`
        },
        uploader: {
            async uploadByFile(file) {
                return directUploader(file);
            },
            async uploadByUrl(url) {
                try {
                    // CORS 정책을 통과하는 경우 직접 URL에서 blob을 생성하여 업로드
                    const response = await fetch(url);
                    if (!response.ok) throw new Error("CORS error or failed request");
                    const blob = await response.blob();
                    const file = new File([blob], url.split("/").pop());
                    return directUploader(file);
                } catch (error) {
                    // CORS 오류 발생 시 서버를 통해 이미지 업로드
                    console.warn("CORS issue detected. Uploading via server...");
                    const apiResponse = await api.post(`/api/${controller.data.get("space-url")}/notes/upload_image`, { image_url: url });

                    if (apiResponse.data.success) {
                        return {
                            success: 1,
                            file: { url: apiResponse.data.url }
                        };
                    } else {
                        // 컨트롤러에 있는 에디터 인스턴스의 notifier를 사용
                        controller.editor.notifier.show({
                            message: apiResponse.data.error,
                            style: 'error',
                        });
                        return { success: 0 };
                    }
                }
            }
        }
    };

    return imageToolConfig;
}